import { Box, Grid2, Typography } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModeCard, SliderWithInput } from '../components';
import { Mode, VideoQuantity } from '../models';
import { useGetConfigQuery } from '../services';

export const Dashboard: FC = () => {
  const { t } = useTranslation();
  const { data: config } = useGetConfigQuery();

  const formik = useFormik<VideoQuantity>({
    initialValues: {
      sdSingle: 0,
      hdSingle: 0,
      sdAbr: 0,
      hdAbr: 0,
      mode: [Mode.Transcoding],
    },
    onSubmit: () => undefined,
    validate: () => ({}),
  });

  const cards = useMemo(() => {
    const values = formik.values;
    if (config) {
      if (values.mode.includes(Mode.Transcoding)) {
        const sdSingleCards = (values.sdSingle || 0) / config.sdSingle;
        const hdSingleCards = (values.hdSingle || 0) / config.hdSingle;
        const sdAbrCards = (values.sdAbr || 0) / config.sdAbr;
        const hdAbrCards = (values.hdAbr || 0) / config.hdAbr;

        const totalCards = sdSingleCards + hdSingleCards + sdAbrCards + hdAbrCards;
        const typesCount = [sdSingleCards, hdSingleCards, sdAbrCards, hdAbrCards].reduce(
          (count, value) => (value > 0 ? count + 1 : count),
          0,
        );
        const extraBuffer = Math.max(0, typesCount - 1) * 0.05;
        const result = Math.ceil((1 + extraBuffer) * totalCards);

        return result;
      } else if (values.mode.includes(Mode.Packaging)) {
        const totalChannel = (values.sdAbr || 0) + (values.hdAbr || 0);
        return Math.ceil(totalChannel / config.packaging);
      }
    }
    return 0;
  }, [formik.values, config]);

  const isOnlyPackagingMode = formik.values.mode.length === 1 && formik.values.mode.includes(Mode.Packaging);

  return (
    <FormikProvider value={formik}>
      <Box display="flex" flexDirection="column" gap="20px">
        <Grid2 container columnSpacing="20px" rowSpacing="60px" alignItems="center">
          <Grid2
            size={{ xs: 12, sm: 12, md: 4 }}
            display="flex"
            justifyContent="center"
            flexDirection="column"
            gap="20px"
          >
            <SliderWithInput name="sdSingle" label={t('calculator.type.sdSingle')} disabled={isOnlyPackagingMode} />
            <SliderWithInput name="hdSingle" label={t('calculator.type.hdSingle')} disabled={isOnlyPackagingMode} />
            <SliderWithInput name="sdAbr" label={t('calculator.type.sdAbr')} />
            <SliderWithInput name="hdAbr" label={t('calculator.type.hdAbr')} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 4 }} display="flex" justifyContent="center">
            <Box display="flex" flexDirection="column" gap="20px">
              <ModeCard
                mode={Mode.Transcoding}
                label={t('calculator.mode.transcoding.label')}
                description={t('calculator.mode.transcoding.description')}
              />
              <ModeCard
                mode={Mode.Packaging}
                label={t('calculator.mode.packaging.label')}
                description={t('calculator.mode.packaging.description')}
              />
            </Box>
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 12, md: 4 }}
            display="flex"
            justifyContent="center"
            bgcolor="primary.20"
            padding="40px 0"
            borderRadius="20px"
          >
            <Box display="flex" alignItems="center" flexDirection="column" gap="20px">
              <Typography textTransform="uppercase">{t('calculator.amount.chassis')}</Typography>
              <Typography variant="h3">{Math.ceil(cards / 6)}</Typography>
              <Typography textTransform="uppercase">{t('calculator.amount.cards')}</Typography>
              <Typography variant="h3">{cards}</Typography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </FormikProvider>
  );
};
